import React, { useState } from 'react';
import { 
  Card, 
  Table, 
  Button, 
  Space, 
  Tag, 
  Input, 
  Select, 
  Modal,
  Form,
  message,
  Typography,
  Avatar,
  Tooltip,
  Popconfirm,
  Descriptions,
  Switch
} from 'antd';
import { 
  UserOutlined, 
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  SearchOutlined,
  FilterOutlined,
  DeleteOutlined,
  KeyOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import dayjs from 'dayjs';
import { useAuthStore } from '../../stores/authStore';

const { Search } = Input;
const { Option } = Select;
const { Title, Text } = Typography;

const UserManagement = () => {
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuthStore();
  
  const [searchText, setSearchText] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();

  // 获取用户列表
  const { data: usersData, isLoading } = useQuery(
    ['users', searchText, roleFilter, statusFilter],
    () => axios.get('/users', {
      params: {
        search: searchText || undefined,
        role: roleFilter || undefined,
        status: statusFilter || undefined,
        limit: 100
      }
    }).then(res => res.data.data),
    { refetchInterval: 30000 }
  );

  // 创建用户
  const createUserMutation = useMutation(
    (userData) => axios.post('/users', userData),
    {
      onSuccess: () => {
        message.success('用户创建成功');
        queryClient.invalidateQueries('users');
        setIsCreateModalVisible(false);
        createForm.resetFields();
      },
      onError: (error) => {
        message.error(error.response?.data?.message || '创建失败');
      }
    }
  );

  // 更新用户信息
  const updateUserMutation = useMutation(
    ({ userId, userData }) => axios.put(`/users/${userId}`, userData),
    {
      onSuccess: () => {
        message.success('用户信息更新成功');
        queryClient.invalidateQueries('users');
        setIsEditModalVisible(false);
        editForm.resetFields();
      },
      onError: (error) => {
        message.error(error.response?.data?.message || '更新失败');
      }
    }
  );

  // 删除用户
  const deleteUserMutation = useMutation(
    (userId) => axios.delete(`/users/${userId}`),
    {
      onSuccess: () => {
        message.success('用户删除成功');
        queryClient.invalidateQueries('users');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || '删除失败');
      }
    }
  );

  /**
   * 函数：resetPasswordMutation 重置指定用户密码为默认值
   * 说明：调用后端 /users/:id/reset-password 接口，将目标用户密码重置为 123456
   */
  const resetPasswordMutation = useMutation(
    (userId) => axios.post(`/users/${userId}/reset-password`),
    {
      onSuccess: () => {
        message.success('密码已重置为 123456');
        queryClient.invalidateQueries('users');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || '重置失败');
      }
    }
  );

  const handleCreateUser = () => {
    setIsCreateModalVisible(true);
  };

  const handleCreateSubmit = async (values) => {
    // 确保数字字段为数字类型
    const userData = {
      ...values,
      dailyCost: values.dailyCost ? parseFloat(values.dailyCost) : values.dailyCost,
      designLevelCoefficient: values.designLevelCoefficient ? parseFloat(values.designLevelCoefficient) : values.designLevelCoefficient
    };
    
    createUserMutation.mutate(userData);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    editForm.setFieldsValue({
      realName: user.realName,
      email: user.email,
      position: user.position,
      role: user.role,
      status: user.status,
      dailyCost: user.dailyCost,
      phone: user.phone,
      designLevelCoefficient: user.designLevelCoefficient
    });
    setIsEditModalVisible(true);
  };

  const handleUpdateUser = async (values) => {
    // 确保数字字段为数字类型
    const userData = {
      ...values,
      dailyCost: values.dailyCost ? parseFloat(values.dailyCost) : values.dailyCost,
      designLevelCoefficient: values.designLevelCoefficient ? parseFloat(values.designLevelCoefficient) : values.designLevelCoefficient
    };
    
    updateUserMutation.mutate({
      userId: editingUser.id,
      userData
    });
  };

  const handleDeleteUser = (user) => {
    if (user.id === currentUser.id) {
      message.warning('不能删除自己的账户');
      return;
    }
    
    deleteUserMutation.mutate(user.id);
  };

  /**
   * 函数：handleResetPassword 触发指定用户的密码重置
   * 参数：user（表格行数据）
   * 行为：调用 resetPasswordMutation，将密码重置为 123456
   */
  const handleResetPassword = (user) => {
    resetPasswordMutation.mutate(user.id);
  };

  const handleViewUser = (user) => {
    setSelectedUser(user);
    setIsDetailModalVisible(true);
  };

  const getRoleText = (role) => {
    const roleMap = {
      admin: '系统管理员',
      manager: '设计主管',
      designer: '设计师',
      pm: '项目经理'
    };
    return roleMap[role] || role;
  };

  const getRoleColor = (role) => {
    const colorMap = {
      admin: 'red',
      manager: 'orange',
      designer: 'blue',
      pm: 'green'
    };
    return colorMap[role] || 'default';
  };

  const getStatusColor = (status) => {
    return status === 'active' ? 'success' : 'default';
  };

  const getStatusText = (status) => {
    return status === 'active' ? '激活' : '禁用';
  };

  const columns = [
    {
      title: '头像',
      dataIndex: 'avatar',
      key: 'avatar',
      width: 60,
      render: (avatar, record) => (
        <Avatar 
          src={avatar} 
          icon={<UserOutlined />}
          size="default"
        />
      ),
    },
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
      width: 120,
      render: (text, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{text}</Text>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            {record.realName}
          </Text>
        </Space>
      ),
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
      width: 200,
    },
    {
      title: '职位',
      dataIndex: 'position',
      key: 'position',
      width: 120,
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      width: 100,
      render: (role) => (
        <Tag color={getRoleColor(role)}>
          {getRoleText(role)}
        </Tag>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: '日成本',
      dataIndex: 'dailyCost',
      key: 'dailyCost',
      width: 100,
      render: (cost) => `¥${cost || 0}`,
    },
    {
      title: '设计系数',
      dataIndex: 'designLevelCoefficient',
      key: 'designLevelCoefficient',
      width: 100,
      render: (coefficient) => coefficient ? parseFloat(coefficient).toFixed(2) : '1.00',
    },
    {
      title: '注册时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date) => dayjs(date).format('YYYY-MM-DD'),
    },
    {
      title: '最后登录',
      dataIndex: 'lastLoginAt',
      key: 'lastLoginAt',
      width: 120,
      render: (date) => date ? dayjs(date).format('MM-DD HH:mm') : '未登录',
    },
    {
      title: '操作',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="查看详情">
            <Button 
              type="link" 
              icon={<EyeOutlined />}
              onClick={() => handleViewUser(record)}
              size="small"
            />
          </Tooltip>
          {currentUser?.role === 'admin' && (
            <>
              <Tooltip title="编辑用户">
                <Button 
                  type="link" 
                  icon={<EditOutlined />}
                  onClick={() => handleEditUser(record)}
                  size="small"
                />
              </Tooltip>
              <Popconfirm
                title="确定要重置该用户的密码为 123456？"
                description="重置后用户需使用新密码登录"
                onConfirm={() => handleResetPassword(record)}
                okText="确定"
                cancelText="取消"
              >
                <Tooltip title="重置密码">
                  <Button
                    type="link"
                    icon={<KeyOutlined />}
                    size="small"
                  />
                </Tooltip>
              </Popconfirm>
              <Popconfirm
                title="确定要删除这个用户吗？"
                description="删除后用户将无法登录系统"
                onConfirm={() => handleDeleteUser(record)}
                okText="确定"
                cancelText="取消"
                disabled={record.id === currentUser.id}
              >
                <Tooltip title={record.id === currentUser.id ? "不能删除自己" : "删除用户"}>
                  <Button 
                    type="link" 
                    danger
                    icon={<DeleteOutlined />}
                    size="small"
                    disabled={record.id === currentUser.id}
                  />
                </Tooltip>
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>用户管理</Title>
      
      {/* 操作栏 */}
      <Card style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space wrap>
            <Search
              placeholder="搜索用户名、姓名或邮箱"
              allowClear
              style={{ width: 250 }}
              onSearch={setSearchText}
              onChange={(e) => !e.target.value && setSearchText('')}
            />
            <Select
              placeholder="选择角色"
              allowClear
              style={{ width: 120 }}
              value={roleFilter}
              onChange={setRoleFilter}
            >
              <Option value="admin">系统管理员</Option>
              <Option value="manager">设计主管</Option>
              <Option value="designer">设计师</Option>
              <Option value="pm">项目经理</Option>
            </Select>
            <Select
              placeholder="选择状态"
              allowClear
              style={{ width: 100 }}
              value={statusFilter}
              onChange={setStatusFilter}
            >
              <Option value="active">激活</Option>
              <Option value="inactive">禁用</Option>
            </Select>
          </Space>
          
          {currentUser?.role === 'admin' && (
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={handleCreateUser}
            >
              新增用户
            </Button>
          )}
        </div>
      </Card>

      {/* 用户列表 */}
      <Card>
        <Table
          columns={columns}
          dataSource={usersData?.users || []}
          rowKey="id"
          loading={isLoading}
          pagination={{
            total: usersData?.pagination?.total || 0,
            pageSize: 50,
            showSizeChanger: false,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 个用户`,
          }}
          scroll={{ x: 1200 }}
        />
      </Card>

      {/* 新增用户模态框 */}
      <Modal
        title="新增用户"
        open={isCreateModalVisible}
        onCancel={() => {
          setIsCreateModalVisible(false);
          createForm.resetFields();
        }}
        onOk={() => createForm.submit()}
        confirmLoading={createUserMutation.isLoading}
        width={600}
      >
        <Form
          form={createForm}
          layout="vertical"
          onFinish={handleCreateSubmit}
        >
          <Form.Item
            name="username"
            label="用户名"
            rules={[
              { required: true, message: '请输入用户名' },
              { min: 3, max: 50, message: '用户名长度应在3-50字符之间' },
              { pattern: /^[a-zA-Z0-9_]+$/, message: '用户名只能包含字母、数字和下划线' }
            ]}
          >
            <Input placeholder="请输入用户名" />
          </Form.Item>

          <Form.Item
            name="email"
            label="邮箱"
            rules={[
              { required: true, message: '请输入邮箱' },
              { type: 'email', message: '请输入有效的邮箱地址' }
            ]}
          >
            <Input placeholder="请输入邮箱" />
          </Form.Item>

          <Form.Item
            name="password"
            label="密码"
            rules={[
              { required: true, message: '请输入密码' },
              { min: 6, message: '密码长度至少6位' }
            ]}
          >
            <Input.Password placeholder="请输入密码" />
          </Form.Item>

          <Form.Item
            name="realName"
            label="真实姓名"
            rules={[
              { required: true, message: '请输入真实姓名' },
              { min: 2, max: 50, message: '姓名长度应在2-50字符之间' }
            ]}
          >
            <Input placeholder="请输入真实姓名" />
          </Form.Item>

          <Form.Item
            name="position"
            label="职位"
            rules={[{ required: true, message: '请输入职位' }]}
          >
            <Input placeholder="请输入职位" />
          </Form.Item>

          <Form.Item
            name="role"
            label="角色"
            rules={[{ required: true, message: '请选择角色' }]}
            initialValue="designer"
          >
            <Select placeholder="请选择角色">
              <Option value="admin">系统管理员</Option>
              <Option value="manager">设计主管</Option>
              <Option value="designer">设计师</Option>
              <Option value="pm">项目经理</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="dailyCost"
            label="日成本（元）"
            rules={[
              { 
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  const num = parseFloat(value);
                  if (isNaN(num) || num < 0) {
                    return Promise.reject(new Error('日成本必须为非负数'));
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Input type="number" placeholder="请输入日成本" />
          </Form.Item>

          <Form.Item
            name="phone"
            label="手机号"
            rules={[
              { pattern: /^1[3-9]\d{9}$/, message: '请输入有效的手机号码' }
            ]}
          >
            <Input placeholder="请输入手机号" />
          </Form.Item>

          <Form.Item
            name="designLevelCoefficient"
            label="设计水平系数"
            rules={[
              { 
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  const num = parseFloat(value);
                  if (isNaN(num) || num < 0.5 || num > 2.0) {
                    return Promise.reject(new Error('设计水平系数范围为0.50-2.00'));
                  }
                  return Promise.resolve();
                }
              }
            ]}
            initialValue={1.00}
          >
            <Input 
              type="number" 
              step="0.01" 
              min="0.50" 
              max="2.00" 
              placeholder="请输入设计水平系数（0.50-2.00）" 
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 编辑用户模态框 */}
      <Modal
        title="编辑用户信息"
        open={isEditModalVisible}
        onCancel={() => {
          setIsEditModalVisible(false);
          editForm.resetFields();
        }}
        onOk={() => editForm.submit()}
        confirmLoading={updateUserMutation.isLoading}
        width={600}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleUpdateUser}
        >
          <Form.Item
            name="realName"
            label="真实姓名"
            rules={[
              { required: true, message: '请输入真实姓名' },
              { min: 2, max: 50, message: '姓名长度应在2-50字符之间' }
            ]}
          >
            <Input placeholder="请输入真实姓名" />
          </Form.Item>

          <Form.Item
            name="email"
            label="邮箱"
            rules={[
              { required: true, message: '请输入邮箱' },
              { type: 'email', message: '请输入有效的邮箱地址' }
            ]}
          >
            <Input placeholder="请输入邮箱" />
          </Form.Item>

          <Form.Item
            name="position"
            label="职位"
            rules={[{ required: true, message: '请输入职位' }]}
          >
            <Input placeholder="请输入职位" />
          </Form.Item>

          <Form.Item
            name="role"
            label="角色"
            rules={[{ required: true, message: '请选择角色' }]}
          >
            <Select placeholder="请选择角色">
              <Option value="admin">系统管理员</Option>
              <Option value="manager">设计主管</Option>
              <Option value="designer">设计师</Option>
              <Option value="pm">项目经理</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="status"
            label="状态"
            rules={[{ required: true, message: '请选择状态' }]}
          >
            <Select placeholder="请选择状态">
              <Option value="active">激活</Option>
              <Option value="inactive">禁用</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="dailyCost"
            label="日成本（元）"
            rules={[
              { 
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  const num = parseFloat(value);
                  if (isNaN(num) || num < 0) {
                    return Promise.reject(new Error('日成本必须为非负数'));
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Input type="number" placeholder="请输入日成本" />
          </Form.Item>

          <Form.Item
            name="phone"
            label="手机号"
            rules={[
              { pattern: /^1[3-9]\d{9}$/, message: '请输入有效的手机号码' }
            ]}
          >
            <Input placeholder="请输入手机号" />
          </Form.Item>

          <Form.Item
            name="designLevelCoefficient"
            label="设计水平系数"
            rules={[
              { 
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  const num = parseFloat(value);
                  if (isNaN(num) || num < 0.5 || num > 2.0) {
                    return Promise.reject(new Error('设计水平系数范围为0.50-2.00'));
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Input 
              type="number" 
              step="0.01" 
              min="0.50" 
              max="2.00" 
              placeholder="请输入设计水平系数（0.50-2.00）" 
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* 用户详情模态框 */}
      <Modal
        title="用户详情"
        open={isDetailModalVisible}
        onCancel={() => setIsDetailModalVisible(false)}
        footer={null}
        width={600}
      >
        {selectedUser && (
          <Descriptions column={1} bordered>
            <Descriptions.Item label="头像">
              <Avatar 
                src={selectedUser.avatar} 
                icon={<UserOutlined />}
                size="large"
              />
            </Descriptions.Item>
            <Descriptions.Item label="用户名">
              {selectedUser.username}
            </Descriptions.Item>
            <Descriptions.Item label="真实姓名">
              {selectedUser.realName}
            </Descriptions.Item>
            <Descriptions.Item label="邮箱">
              {selectedUser.email}
            </Descriptions.Item>
            <Descriptions.Item label="职位">
              {selectedUser.position}
            </Descriptions.Item>
            <Descriptions.Item label="角色">
              <Tag color={getRoleColor(selectedUser.role)}>
                {getRoleText(selectedUser.role)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color={getStatusColor(selectedUser.status)}>
                {getStatusText(selectedUser.status)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="日成本">
              ¥{selectedUser.dailyCost || 0}
            </Descriptions.Item>
            <Descriptions.Item label="设计水平系数">
              {selectedUser.designLevelCoefficient ? parseFloat(selectedUser.designLevelCoefficient).toFixed(2) : '1.00'}
            </Descriptions.Item>
            <Descriptions.Item label="手机号">
              {selectedUser.phone || '未填写'}
            </Descriptions.Item>
            <Descriptions.Item label="注册时间">
              {dayjs(selectedUser.createdAt).format('YYYY-MM-DD HH:mm:ss')}
            </Descriptions.Item>
            <Descriptions.Item label="最后登录">
              {selectedUser.lastLoginAt 
                ? dayjs(selectedUser.lastLoginAt).format('YYYY-MM-DD HH:mm:ss')
                : '未登录'
              }
            </Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </div>
  );
};

export default UserManagement;
